/**
 * Virtueller Joystick fuer die Bedienung am Handy (Masterprompt Abschnitte 43, 81).
 *
 * Bedienprinzip:
 * Der Spieler tippt IRGENDWO in die linke Bildschirmhaelfte - dort erscheint der
 * Joystick. Das ist auf Mobilgeraeten deutlich angenehmer als ein fester Punkt,
 * weil man nicht hinschauen muss, um den Daumen richtig zu setzen.
 *
 * Auf dem Desktop bleibt der Joystick unsichtbar; dort wird mit WASD gesteuert.
 *
 * Tippen statt Ziehen:
 * Der Joystick liegt ueber der halben Spielflaeche - ein Tippen auf einen Gegner
 * wuerde sonst immer nur den Joystick aufrufen. Deshalb wird unterschieden:
 * Ein kurzes Antippen ohne nennenswerte Bewegung ist KEINE Laufeingabe, sondern
 * wird als Tippen auf die Spielflaeche weitergereicht - damit laesst sich auch
 * links ein Ziel auswaehlen.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { isTap } from '../input/gestures.js';
import { JOYSTICK_DEAD_ZONE, joystickRichtung } from '../input/joystick.js';
import type { InputController } from '../input/InputController.js';

/**
 * Wie weit der Knopf vom Mittelpunkt wandert, in Bildschirmpunkten.
 *
 * Nur noch fuer die ANZEIGE (23.10.2026): Seit die Eingabe alles oder
 * nichts ist, haengt am Radius kein Tempo mehr. Der Knopf zeigt, wohin
 * der Daumen zeigt, und bleibt am Rand stehen.
 */
const JOYSTICK_RADIUS = 56;

interface JoystickPose {
  originX: number;
  originY: number;
  knobX: number;
  knobY: number;
}

interface Props {
  input: InputController;
  /** Wird gerufen, wenn die Beruehrung nur ein Tippen war. */
  onTap: (screenX: number, screenY: number) => void;
}

export function VirtualJoystick({ input, onTap }: Props) {
  const [pose, setPose] = useState<JoystickPose | null>(null);
  const pointerId = useRef<number | null>(null);
  /** Beginn und groesste Auslenkung der laufenden Beruehrung. */
  const gesture = useRef({ startedAt: 0, maxDistance: 0 });
  /** Meldet die Zuhoerer am Fenster wieder ab. */
  const abbruch = useRef<AbortController | null>(null);

  /** Rechnet die Fingerposition in eine Richtung um und meldet sie weiter. */
  const updateFromPointer = useCallback(
    (originX: number, originY: number, pointerX: number, pointerY: number) => {
      const dx = pointerX - originX;
      const dy = pointerY - originY;
      const distance = Math.hypot(dx, dy);

      /*
       * ALLES ODER NICHTS (Produktentscheidung vom 23.10.2026).
       *
       * Wie weit der Daumen ausgelenkt ist, entscheidet nur ueber die
       * Richtung. Vorher war die Auslenkung ein Gaspedal - und die
       * Laufanimation, die nur EIN Tempo kennt, sah dabei aus wie
       * Zeitlupe. Die Regel steht in input/joystick.ts.
       */
      const richtung = joystickRichtung(dx, dy);
      input.setJoystickFromScreen(richtung.x, richtung.y);

      /*
       * Der KNOPF folgt weiter dem Daumen - er zeigt die Richtung, nicht
       * das Tempo. Am Rand bleibt er stehen.
       */
      if (distance < JOYSTICK_DEAD_ZONE) {
        setPose({ originX, originY, knobX: 0, knobY: 0 });
        return;
      }

      const clamped = Math.min(distance, JOYSTICK_RADIUS);
      setPose({
        originX,
        originY,
        knobX: (dx / distance) * clamped,
        knobY: (dy / distance) * clamped,
      });
    },
    [input],
  );

  /** Beendet eine laufende Beruehrung - von ueberall aus aufrufbar. */
  const stoppe = useCallback(() => {
    pointerId.current = null;
    input.setJoystickFromScreen(0, 0);
    setPose(null);
    abbruch.current?.abort();
    abbruch.current = null;
  }, [input]);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (pointerId.current !== null) return;
      pointerId.current = event.pointerId;
      gesture.current = { startedAt: performance.now(), maxDistance: 0 };
      event.currentTarget.setPointerCapture(event.pointerId);
      updateFromPointer(event.clientX, event.clientY, event.clientX, event.clientY);

      /*
       * Sicherheitsnetz am Fenster (im Spiel aufgefallen am 24.09.2026).
       *
       * Das Loslassen kann den Joystick verfehlen: Er verschwindet zum
       * Beispiel beim Wechsel in die Enemy View oder am Matchende mitten in
       * der Bewegung. Ohne dieses Netz bliebe die zuletzt gemeldete Richtung
       * stehen - der Hero liefe dann endlos weiter geradeaus.
       */
      abbruch.current?.abort();
      const controller = new AbortController();
      abbruch.current = controller;
      /*
       * Nur der EIGENE Finger zaehlt (im Spiel aufgefallen am 24.09.2026):
       * Wer links laeuft und rechts einen Skill zielt, hebt irgendwann den
       * rechten Finger. Ohne diese Pruefung galt das als "losgelassen" und der
       * Hero blieb stehen, obwohl der linke Daumen noch auf dem Schirm lag.
       */
      const beendet = (event: PointerEvent) => {
        if (event.pointerId === pointerId.current) stoppe();
      };
      window.addEventListener('pointerup', beendet, { signal: controller.signal });
      window.addEventListener('pointercancel', beendet, { signal: controller.signal });
    },
    [updateFromPointer, stoppe],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (pointerId.current !== event.pointerId || !pose) return;

      // Die groesste Auslenkung merken - nicht die aktuelle. Wer weit zieht und
      // wieder zurueckkommt, wollte laufen und nicht tippen.
      const distance = Math.hypot(event.clientX - pose.originX, event.clientY - pose.originY);
      gesture.current.maxDistance = Math.max(gesture.current.maxDistance, distance);

      updateFromPointer(pose.originX, pose.originY, event.clientX, event.clientY);
    },
    [pose, updateFromPointer],
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (pointerId.current !== event.pointerId) return;
      stoppe();

      // War es nur ein kurzes Antippen? Dann galt es der Spielflaeche.
      const dauer = performance.now() - gesture.current.startedAt;
      if (isTap(dauer, gesture.current.maxDistance)) {
        onTap(event.clientX, event.clientY);
      }
    },
    [stoppe, onTap],
  );

  /*
   * Zweites Sicherheitsnetz: eine Maus, die AUSSERHALB des Fensters losgelassen
   * wurde (im Spiel aufgefallen am 24.09.2026 - der Hero lief danach endlos
   * geradeaus, und keine Taste half mehr, weil der Joystick Vorrang vor der
   * Tastatur hat).
   *
   * Beim naechsten Mausweg im Fenster verraet "buttons === 0", dass gar keine
   * Taste mehr gedrueckt ist. Dann gilt die Bewegung als beendet. Fuer Finger
   * gilt das nicht: Ein abgehobener Finger erzeugt keine Bewegung mehr.
   */
  useEffect(() => {
    const pruefe = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return;
      if (pointerId.current === null || event.buttons !== 0) return;
      pointerId.current = null;
      input.setJoystickFromScreen(0, 0);
      setPose(null);
    };
    window.addEventListener('pointermove', pruefe);
    return () => window.removeEventListener('pointermove', pruefe);
  }, [input]);

  // Sicherheitsnetz: Geht die Seite in den Hintergrund oder verschwindet der
  // Joystick mitten in der Bewegung, darf der Hero nicht endlos weiterlaufen.
  useEffect(() => {
    const stop = () => {
      pointerId.current = null;
      input.setJoystickFromScreen(0, 0);
      setPose(null);
    };
    window.addEventListener('blur', stop);
    document.addEventListener('visibilitychange', stop);
    return () => {
      window.removeEventListener('blur', stop);
      document.removeEventListener('visibilitychange', stop);
      // Beim Ausbauen der Komponente ebenfalls: sonst bleibt die zuletzt
      // gemeldete Richtung fuer immer stehen.
      stop();
    };
  }, [input]);

  return (
    <div
      className="dl-joystick-area"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {pose && (
        <div
          className="dl-joystick"
          style={{
            left: pose.originX,
            top: pose.originY,
            width: JOYSTICK_RADIUS * 2,
            height: JOYSTICK_RADIUS * 2,
          }}
        >
          <div
            className="dl-joystick__knob"
            style={{ transform: `translate(${pose.knobX}px, ${pose.knobY}px)` }}
          />
        </div>
      )}
    </div>
  );
}
